import {observable, computed, action, makeObservable} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import CalendarEvent from 'main/stores/calendar_events/CalendarEvent';
import {t} from 'shared/core';
import {atTimeOfDay, calendarDay, pickedDay} from 'shared/tools';

const DATE_FORMAT = 'MMM D, YYYY';
const EVENT_COLOUR = '#F5822B';

class CustomCalendarEvent extends CalendarEvent {
  description;
  startsAt;
  endsAt;

  // A timed event is edited as a day plus two times, so the three are tracked separately and
  // composed into startsAt/endsAt. A missing time has to stay missing rather than falling back
  // to midnight, otherwise a blank time input saves as 12:00 AM instead of failing validation.
  eventDate;
  startTime;
  endTime;

  _relations = {
    _dates: ['startsAt', 'endsAt']
  };

  constructor(other) {
    super();

    makeObservable(this, {
      description: observable,
      startsAt: observable,
      endsAt: observable,
      eventDate: observable,
      startTime: observable,
      endTime: observable,
      startDate: computed,
      dateRange: computed,
      backgroundColor: computed,
      textColor: computed,
      borderColor: computed,
      canViewDetails: computed,
      setAllDay: action,
      setStartDate: action,
      setEndDate: action,
      setStartTime: action,
      setEndTime: action,
      composeTimedRange: action
    });

    this.merge(other);

    this.eventDate = withoutTime(this.startsAt) || null;
    this.startTime = timeOfDay(this.startsAt);
    this.endTime = timeOfDay(this.endsAt);
  }

  get backgroundColor() {
    return EVENT_COLOUR;
  }

  get textColor() {
    return '#FFFFFF';
  }

  get borderColor() {
    return EVENT_COLOUR;
  }

  get canViewDetails() {
    return true;
  }

  get startDate() {
    return this.allDay ? this.startsAt : this.eventDate;
  }

  setAllDay(allDay) {
    const day = this.eventDate || withoutTime(this.startsAt) || null;

    if (allDay) {
      this.merge({allDay, startsAt: day, endsAt: day});
      return;
    }

    this.eventDate = day;
    this.startTime = '';
    this.endTime = '';
    this.merge({allDay, startsAt: null, endsAt: null});
  }

  setStartDate(date) {
    if (this.allDay) {
      this.merge({startsAt: withDateOf(this.startsAt, date)});
      return;
    }

    this.eventDate = withDateOf(this.eventDate, date);
    this.composeTimedRange();
  }

  setEndDate(date) {
    this.merge({endsAt: withDateOf(this.endsAt, date)});
  }

  setStartTime(time) {
    this.startTime = time;
    this.composeTimedRange();
  }

  setEndTime(time) {
    this.endTime = time;
    this.composeTimedRange();
  }

  composeTimedRange() {
    this.merge({
      startsAt: atTimeOfDay(this.eventDate, this.startTime),
      endsAt: atTimeOfDay(this.eventDate, this.endTime)
    });
  }

  hasDate(name, other) {
    if (this.allDay && _.isString(other[name]) && _.includes(['startsAt', 'endsAt'], name)) {
      this[name] = calendarDay(other[name]);
      return;
    }

    super.hasDate(name, other);
  }

  toFullCalendarEvent() {
    return _.omit(
      super.toFullCalendarEvent(),
      ['startsAt', 'endsAt', 'description', 'eventDate', 'startTime', 'endTime']
    );
  }

  toJS() {
    const attributes = _.omit(super.toJS(), ['eventDate', 'startTime', 'endTime']);

    if (!this.allDay) return attributes;

    return {
      ...attributes,
      startsAt: calendarDate(this.startsAt),
      endsAt: calendarDate(this.endsAt)
    };
  }

  get dateRange() {
    if (!this.startsAt || !this.endsAt) return null;

    const start = moment(this.startsAt);
    const end = moment(this.endsAt);

    if (this.allDay) {
      return start.isSame(end, 'day') ?
        start.format(DATE_FORMAT) :
        dateSpan(start.format(DATE_FORMAT), end.format(DATE_FORMAT));
    }

    const timeFormat = t('components.dates.TIMESTAMP_FORMAT');

    if (start.isSame(end, 'day')) {
      return `${start.format(DATE_FORMAT)}, ` +
        `${start.format(timeFormat)} - ${end.format(timeFormat)}`;
    }

    return dateSpan(
      `${start.format(DATE_FORMAT)}, ${start.format(timeFormat)}`,
      `${end.format(DATE_FORMAT)}, ${end.format(timeFormat)}`
    );
  }
}

function startOfToday() {
  return moment().startOf('day');
}

function calendarDate(value) {
  return value ? moment(value).format('YYYY-MM-DD') : value;
}

function withoutTime(date) {
  return date ? moment(date).startOf('day').toDate() : date;
}

function withDateOf(current, date) {
  if (!date) return null;

  const nextDate = pickedDay(date);

  return (current ? moment(current) : startOfToday()).set({
    year: nextDate.year(),
    month: nextDate.month(),
    date: nextDate.date()
  }).toDate();
}

function timeOfDay(date) {
  return date ? moment(date).format(t('components.dates.TIMESTAMP_FORMAT')) : '';
}

function dateSpan(startDate, endDate) {
  return t('components.dates.DATE_SPAN', {startDate, endDate});
}

export default CustomCalendarEvent;
